import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import {
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import api from './api';
import { AVAILABLE_LANGUAGES } from './utils/languages';

export default function AddCategoryForm() {
    const navigate = useNavigate();
    const [currentTab, setCurrentTab] = useState(0);
    const [addLanguageDialog, setAddLanguageDialog] = useState(false);
    const [newLanguageName, setNewLanguageName] = useState('');
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Default languages
    const defaultLanguages = AVAILABLE_LANGUAGES;

    const [formData, setFormData] = useState({
        name: '',
        image: '',
        imageUploading: false,
        sortingPriority: 0,
        languageSpec: {
            ...defaultLanguages.reduce((acc, lang) => {
                acc[lang] = { name: '', image: '' };
                return acc;
            }, {})
        }
    });

    // Handle input change for general fields
    const handleInputChange = (field, value) => {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    // Handle language specific change
    const handleLanguageChange = (language, field, value) => {
        setFormData({
            ...formData,
            languageSpec: {
                ...formData.languageSpec,
                [language]: {
                    ...formData.languageSpec[language],
                    [field]: value
                }
            }
        });
    };

    // Handle main image upload
    const handleMainImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Start loading state for image upload
        setFormData(prev => ({
            ...prev,
            imageUploading: true
        }));

        const formDataObj = new FormData();
        formDataObj.append("image", file);

        // Implementation with API call
        api.post('/upload-image', formDataObj, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then(response => {
            if (response.data && response.data.success) {
                const imageUrl = response.data.url;
                handleInputChange('image', imageUrl);
            } else {
                throw new Error(response.data?.error || 'Image upload failed');
            }
        })
        .catch(error => {
            console.error("Error uploading image:", error);
            showSnackbar(`Error uploading image: ${error.message}`, "error");
            
            // Fallback to local preview for development
            const imageUrl = URL.createObjectURL(file);
            handleInputChange('image', imageUrl);
        })
        .finally(() => {
            // Clear loading state
            setFormData(prev => ({
                ...prev,
                imageUploading: false
            }));
        });
    };

    // Handle image upload for a language
    const handleLanguageImageUpload = (language, e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Set loading state for this specific image
        setFormData((prev) => ({
            ...prev,
            languageSpec: {
                ...prev.languageSpec,
                [language]: {
                    ...prev.languageSpec[language],
                },
            },
        }));

        const formDataObj = new FormData();
        formDataObj.append("image", file);
        formDataObj.append("language", language);

        // Implementation with API call
        api.post('/upload-image', formDataObj, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then(response => {
            if (response.data && response.data.success) {
                const imageUrl = response.data.url;
                handleLanguageChange(language, "image", imageUrl);
                
                // If this is the first image, use it as the main image
                if (!formData.image) {
                    handleInputChange("image", imageUrl);
                }
            } else {
                throw new Error(response.data?.error || 'Image upload failed');
            }
        })
        .catch(error => {
            console.error("Error uploading image:", error);
            showSnackbar(`Error uploading image: ${error.message}`, "error");
            
            // Fallback to local preview for development
            const imageUrl = URL.createObjectURL(file);
            handleLanguageChange(language, "image", imageUrl);
            
            // If this is the first image, use it as the main image
            if (!formData.image) {
                handleInputChange("image", imageUrl);
            }
        })
        .finally(() => {
            // Clear loading state
            setFormData((prev) => ({
                ...prev,
                languageSpec: {
                    ...prev.languageSpec,
                    [language]: {
                        ...prev.languageSpec[language],
                    },
                },
            }));
        });
    };

    // Handle tab change for languages
    const handleTabChange = (event, newValue) => {
        if (newValue === Object.keys(formData.languageSpec).length) {
            // "+" tab was clicked
            setAddLanguageDialog(true);
        } else {
            setCurrentTab(newValue);
        }
    };

    // Handle adding a new language
    const handleAddLanguage = () => {
        if (newLanguageName && !Object.keys(formData.languageSpec).includes(newLanguageName.toLowerCase())) {
            const language = newLanguageName.toLowerCase();

            setFormData({
                ...formData,
                languageSpec: {
                    ...formData.languageSpec,
                    [language]: { name: '', image: ''}
                }
            });

            setNewLanguageName('');
            setAddLanguageDialog(false);

            // Switch to the new language tab
            setCurrentTab(Object.keys(formData.languageSpec).length);
        }
    };

    // Handle removing a language
    const handleRemoveLanguage = (language) => {
        if (defaultLanguages.includes(language)) {
            showSnackbar(`Cannot remove default language: ${language}`, 'error');
            return;
        }

        const updatedLanguageSpec = { ...formData.languageSpec };
        delete updatedLanguageSpec[language];

        setFormData({
            ...formData,
            languageSpec: updatedLanguageSpec
        });

        // Reset tab if needed
        if (currentTab >= Object.keys(updatedLanguageSpec).length) {
            setCurrentTab(0);
        }
    };

    // Validate form before submission
    const validateForm = () => {
        if (!formData.name.trim()) {
            showSnackbar('Category name is required', 'error');
            return false;
        }
        return true;
    };

    // Show snackbar notification
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Save the new category
    const handleSave = async () => {
        if (!validateForm()) return;

        setSaving(true);
        try {
            // Call the backend API to create category
            const response = await api.post(`/category`, formData, {
                headers: {
                    'Content-Type': 'application/json',
                    // 'Authorization': `Bearer ${localStorage.getItem('token')}` // Add token if using JWT
                }
            });
            
            if (response.data.success) {
                showSnackbar(`Category "${formData.name}" created successfully!`);
                // Navigate to categories page or category detail
                setTimeout(() => {
                    navigate(`/categories`);
                }, 1500);
            } else {
                throw new Error(response.data.error || 'Failed to create category');
            }
        } catch (error) {
            console.error('Error creating category:', error);
            showSnackbar(error.message || 'Failed to create category', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h5">Add New Category</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || !formData.name}
                    >
                        {saving ? 'Saving...' : 'Save Category'}
                    </Button>
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>General Information</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!formData.name && formData.touched?.name}
                                helperText={!formData.name && formData.touched?.name ? 'Name is required' : ''}
                                onBlur={() => setFormData(prev => ({ 
                                    ...prev, 
                                    touched: { ...prev.touched, name: true } 
                                }))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sorting Priority"
                                type="number"
                                value={formData.sortingPriority}
                                onChange={(e) => handleInputChange('sortingPriority', parseInt(e.target.value) || 0)}
                                helperText="Lower numbers appear first"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                sx={{ height: '56px' }}
                                disabled={formData.imageUploading}
                            >
                                {formData.imageUploading ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    'Upload Category Image'
                                )}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleMainImageUpload}
                                />
                            </Button>
                        </Grid>
                        
                    </Grid>
                    {formData.image && (
                            <Grid item xs={12}>
                                <Box sx={{ mt: 2, maxWidth: '150px' }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Category Image:
                                    </Typography>
                                    <img
                                        src={formData.image}
                                        alt="Category preview"
                                        style={{ width: '100%', borderRadius: '4px' }}
                                    />
                                </Box>
                            </Grid>
                        )}
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Language-Specific Information</Typography>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {Object.keys(formData.languageSpec).map(lang => (
                                <Tab
                                    key={lang}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                                            {!defaultLanguages.includes(lang) && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveLanguage(lang);
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    }
                                />
                            ))}
                            <Tab icon={<AddIcon />} />
                        </Tabs>
                    </Box>

                    {Object.keys(formData.languageSpec).map((lang, index) => (
                        <Box
                            key={lang}
                            role="tabpanel"
                            hidden={currentTab !== index}
                            sx={{ py: 3 }}
                        >
                            {currentTab === index && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={`Name (${lang})`}
                                            value={formData.languageSpec[lang]?.name || ''}
                                            onChange={(e) => handleLanguageChange(lang, 'name', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            fullWidth
                                            sx={{ height: "56px" }}
                                        >
                                            {formData.languageSpec[lang]?.uploading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                `Upload Image for ${lang.charAt(0).toUpperCase() + lang.slice(1)}`
                                            )}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={(e) => handleLanguageImageUpload(lang, e)}
                                            />
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {formData.languageSpec[lang]?.image && (
                                            <Box sx={{ mt: 2, maxWidth: "300px" }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Current Image for {lang.charAt(0).toUpperCase() + lang.slice(1)}:
                                                </Typography>
                                                <img
                                                    src={formData.languageSpec[lang].image}
                                                    alt={`${lang} preview`}
                                                    style={{ width: "100%", borderRadius: "4px" }}
                                                />
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* Add Language Dialog */}
            <Dialog open={addLanguageDialog} onClose={() => setAddLanguageDialog(false)}>
                <DialogTitle>Add New Language</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Language Name"
                        fullWidth
                        value={newLanguageName}
                        onChange={(e) => setNewLanguageName(e.target.value)}
                        helperText="Example: Spanish, French, Italian, etc."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddLanguageDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddLanguage} variant="contained" color="primary">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}